// Public-side live feed for /electionday.
//
// Reverse-chronological timeline of:
//   • newsroom entries — short text + optional source attribution
//   • social entries   — either an embedUrl we render as a platform
//                        iframe, or raw embedHtml the editor pasted
//                        from a platform's embed widget
//
// Manual publishing only — entries appear here only after the
// editor saves them in the admin composer. There is no automated
// scraping or sync.
//
// Embed HTML is intentionally sanitised lightly: this is an
// editor-only surface (the admin who pasted the embed had to log
// in first), so the threat model is "the editor pastes something
// that breaks layout", not "anonymous user injects XSS". We strip
// <script> tags defensively but otherwise trust the platform's
// embed widget output (oEmbed-style iframes from Twitter/X,
// Instagram, Facebook, etc.).

import { useEffect, useMemo, useRef, useState } from 'react';
import { useElectionDayEditorial } from '../../lib/useElectionDayEditorial';
import { toFeedEmbedSrc, platformLabel } from '../../lib/feedEmbed';
import type { FeedEntry } from '../../data/electionday-editorial-types';

export default function LiveFeed() {
  const { data } = useElectionDayEditorial();
  const entries = useMemo(() => sortByPublishedDesc(data.feed), [data.feed]);

  // "Prominent" while the sentinel sits at the top of the page is in
  // view; "normal" once the reader has scrolled past. A class on the
  // root element drives CSS transitions on heading size, entry
  // padding, and font sizes so the feed visibly grows / shrinks as a
  // single coherent animation. No JS-driven layout on the live-core
  // container itself — keeps the column grid stable.
  const sentinelRef = useRef<HTMLDivElement | null>(null);
  const [prominent, setProminent] = useState(true);
  useEffect(() => {
    const node = sentinelRef.current;
    if (!node || typeof IntersectionObserver === 'undefined') return;
    const observer = new IntersectionObserver(
      ([entry]) => setProminent(entry.isIntersecting),
      { rootMargin: '0px', threshold: 0 },
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  return (
    <section
      className={`electionday-feed${prominent ? ' electionday-feed--prominent' : ''}`}
      aria-label="Live feed"
    >
      {/* Sentinel — observed by IntersectionObserver. When it's in
          view, the feed is "prominent"; once it scrolls past, the
          feed shrinks to its normal size. Zero-height so it doesn't
          affect layout. */}
      <div ref={sentinelRef} className="electionday-feed__sentinel" aria-hidden="true" />

      <header className="electionday-feed__head">
        <h2 className="electionday-feed__title">Live feed</h2>
        <p className="electionday-feed__lede">
          Newsroom updates and social posts, newest first. Manually
          published — every entry on this list has been signed off by
          an editor.
        </p>
      </header>

      {entries.length === 0 ? (
        <p className="electionday-feed__empty">
          The feed is quiet for now. Updates from the newsroom and
          notable social posts will appear here once counting begins.
        </p>
      ) : (
        <ol className="electionday-feed__list">
          {entries.map((e) => (
            <FeedItem key={e.id} entry={e} />
          ))}
        </ol>
      )}
    </section>
  );
}

function FeedItem({ entry }: { entry: FeedEntry }) {
  return (
    <li
      className={`electionday-feed__item electionday-feed__item--${entry.type}`}
    >
      <header className="electionday-feed__item-head">
        <span
          className={`electionday-feed__type-pill electionday-feed__type-pill--${entry.type}`}
        >
          {entry.type === 'newsroom' ? 'Newsroom' : 'Social'}
        </span>
        {entry.source && (
          <span className="electionday-feed__source">{entry.source}</span>
        )}
        <time
          className="electionday-feed__time"
          dateTime={entry.publishedAt}
          title={formatAbsolute(entry.publishedAt)}
        >
          {formatRelative(entry.publishedAt)}
        </time>
      </header>

      {entry.imageUrl && (
        <img
          className="electionday-feed__image"
          src={entry.imageUrl}
          alt=""
          loading="lazy"
        />
      )}

      {entry.text && (
        <p className="electionday-feed__text">{entry.text}</p>
      )}

      {/* Render the embed whenever there's one to render, regardless
          of entry.type. Editors routinely paste an Instagram URL on a
          newsroom entry to add context — gating on type swallowed the
          embed silently. */}
      {(entry.embedHtml || entry.embedUrl) && (
        <SocialEmbed url={entry.embedUrl} html={entry.embedHtml} />
      )}

      {entry.articleUrl && (
        <p className="electionday-feed__article-link">
          <a href={entry.articleUrl} target="_blank" rel="noopener noreferrer">
            Read more →
          </a>
        </p>
      )}
    </li>
  );
}

function SocialEmbed({
  url,
  html,
}: {
  url: string | null;
  html: string | null;
}) {
  // Pasted embed HTML wins — most platforms produce a ready-to-render
  // <blockquote>/<iframe> via their "copy embed code" widget. We only
  // sanitise the obvious XSS vectors (see sanitiseEmbedHtml).
  if (html) {
    return (
      <div
        className="electionday-feed__embed electionday-feed__embed--html"
        dangerouslySetInnerHTML={{ __html: sanitiseEmbedHtml(html) }}
      />
    );
  }
  if (!url) return null;

  // URL → iframe src via the shared helper. Recognises Instagram
  // posts/reels, YouTube, Facebook video/post, X/Twitter status.
  // Falls back to a link card for URLs the helper doesn't recognise
  // so an unknown URL still gives the reader somewhere to click.
  const embedSrc = toFeedEmbedSrc(url);
  if (embedSrc) {
    return (
      <div className="electionday-feed__embed electionday-feed__embed--iframe">
        <iframe
          src={embedSrc}
          title={`Embedded ${platformLabel(url)} post`}
          className="electionday-feed__embed-iframe"
          loading="lazy"
          allow="autoplay; clipboard-write; encrypted-media; picture-in-picture; web-share"
          allowFullScreen
          scrolling="no"
          referrerPolicy="strict-origin-when-cross-origin"
        />
      </div>
    );
  }
  return (
    <div className="electionday-feed__embed electionday-feed__embed--link">
      <a href={url} target="_blank" rel="noopener noreferrer">
        View original {platformLabel(url)} post ↗
      </a>
    </div>
  );
}

function sortByPublishedDesc(feed: FeedEntry[]): FeedEntry[] {
  return [...feed].sort((a, b) =>
    a.publishedAt < b.publishedAt ? 1 : a.publishedAt > b.publishedAt ? -1 : 0,
  );
}

/** Strip the obvious XSS vectors from pasted embed HTML before we
 *  pipe it through dangerouslySetInnerHTML. The threat model here
 *  is a trusted editor pasting platform embed code — we're not
 *  trying to defend against a hostile attacker, just catching
 *  accidents (e.g. copy-pasting a script tag). */
function sanitiseEmbedHtml(html: string): string {
  return html
    // <script> tags — most platforms' embeds rely on an external
    // <script src=…> that we drop. iframe-only embeds (Twitter's
    // "blockquote" embed degrades gracefully without the script,
    // Instagram does too) still render acceptably.
    .replace(/<script\b[\s\S]*?<\/script>/gi, '')
    // on*="…" event handlers
    .replace(/\son\w+="[^"]*"/gi, '')
    .replace(/\son\w+='[^']*'/gi, '')
    // javascript: URLs
    .replace(/(href|src)\s*=\s*"javascript:[^"]*"/gi, '$1="#"')
    .replace(/(href|src)\s*=\s*'javascript:[^']*'/gi, "$1='#'");
}

function formatAbsolute(iso: string): string {
  try {
    return new Date(iso).toLocaleString('en-GB');
  } catch {
    return iso;
  }
}

function formatRelative(iso: string): string {
  try {
    const then = new Date(iso).getTime();
    const now = Date.now();
    const sec = Math.round((now - then) / 1000);
    if (sec < 30) return 'just now';
    if (sec < 60) return `${sec}s ago`;
    const min = Math.round(sec / 60);
    if (min < 60) return `${min}m ago`;
    const hr = Math.round(min / 60);
    if (hr < 24) return `${hr}h ago`;
    return new Date(iso).toLocaleString('en-GB', {
      day: '2-digit',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return iso;
  }
}
